// SEU Grants admin module.
//
// Lets Josh / Trent manage Synthetic Equity Unit grants for directors per the
// WorkVan SEU Plan. Everything is manual per Plan §5.3.3 (Board certification)
// and the user's instruction "nothing is automatic" — the system surfaces
// readiness, the admin clicks to vest / certify.

import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../supabase';

const PLAN_CAP = 250000;

export default function SEU({ adminEmail }) {
  const [directors, setDirectors] = useState([]);
  const [grants, setGrants] = useState([]);
  const [ladders, setLadders] = useState([]);
  const [tranches, setTranches] = useState([]);
  const [events, setEvents] = useState([]);
  const [qcCounts, setQcCounts] = useState({}); // affiliate_id -> qualified customer count
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [dirsRes, grantsRes, laddersRes, tranchesRes, eventsRes] = await Promise.all([
        // Pull anyone tier=director, plus anyone with grants (in case a non-director was granted)
        supabase.from('affiliates').select('id, name, email, tier, code, active').or('tier.eq.director,tier.eq.recruiter').order('name'),
        supabase.from('seu_grants').select('*').order('created_at', { ascending: false }),
        supabase.from('seu_milestone_ladders').select('*').order('display_order'),
        supabase.from('seu_signing_tranches').select('*').order('tranche_number'),
        supabase.from('seu_vesting_events').select('*').order('occurred_at', { ascending: false }).limit(200),
      ]);
      setDirectors(dirsRes.data || []);
      setGrants(grantsRes.data || []);
      setLadders(laddersRes.data || []);
      setTranches(tranchesRes.data || []);
      setEvents(eventsRes.data || []);

      // Fetch qualified-customer count per director via the SQL helper
      const ids = (dirsRes.data || []).map(d => d.id);
      const counts = {};
      await Promise.all(ids.map(async id => {
        const { data } = await supabase.rpc('qualified_customer_count', { p_affiliate_id: id });
        counts[id] = (typeof data === 'number') ? data : (data?.[0] || 0);
      }));
      setQcCounts(counts);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Aggregate stats — what counts as "vested" depends on grant type:
  //  - milestone grants: vested in full when achieved_at is set on the ladder rung
  //  - signing grants: each tranche vests independently when vested_at is set
  const stats = useMemo(() => {
    let granted = 0, vested = 0, forfeited = 0;
    for (const g of grants) {
      if (g.status === 'forfeited') { forfeited += g.shares; continue; }
      granted += g.shares;
      if (g.grant_type === 'milestone') {
        const ladder = ladders.find(l => l.id === g.milestone_ladder_id);
        if (ladder?.achieved_at) vested += g.shares;
      } else {
        const ts = tranches.filter(t => t.grant_id === g.id);
        for (const t of ts) if (t.vested_at && !t.forfeited_at) vested += t.shares;
      }
    }
    return { granted, vested, unvested: granted - vested, forfeited };
  }, [grants, ladders, tranches]);

  const directorsWithActivity = useMemo(() => {
    const grantedSet = new Set(grants.map(g => g.affiliate_id));
    const ladderSet = new Set(ladders.map(l => l.affiliate_id));
    // Surface tier=director first, then anyone with grants/ladders
    return directors.map(d => ({
      ...d,
      hasGrants: grantedSet.has(d.id) || ladderSet.has(d.id),
      qc: qcCounts[d.id] ?? 0,
    }));
  }, [directors, grants, ladders, qcCounts]);

  const selected = useMemo(
    () => directorsWithActivity.find(d => d.id === selectedId) || null,
    [directorsWithActivity, selectedId],
  );

  return (
    <div style={{ padding: '1.5rem', maxWidth: 1200, margin: '0 auto', color: '#e0e0e0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ fontSize: '1.6rem', fontWeight: 700, margin: 0, marginBottom: '0.25rem' }}>SEU Grants</h1>
          <div style={{ color: '#888', fontSize: '0.85rem' }}>Synthetic Equity Units — manage director grants, milestone ladders, and vesting.</div>
        </div>
        <a
          href="https://docs.google.com/document/d/_replace_with_plan_url_/"
          target="_blank"
          rel="noopener"
          style={{ color: '#f0a500', fontSize: '0.8rem', textDecoration: 'underline' }}
        >
          View Plan document →
        </a>
      </div>

      {/* Cap stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <StatCard label="Granted" value={stats.granted.toLocaleString()} sub={`of ${PLAN_CAP.toLocaleString()} cap`} accent="#f0a500" />
        <StatCard label="Vested" value={stats.vested.toLocaleString()} accent="#10B981" />
        <StatCard label="Unvested" value={stats.unvested.toLocaleString()} accent="#888" />
        <StatCard label="Forfeited" value={stats.forfeited.toLocaleString()} accent="#666" />
      </div>

      {/* Director list */}
      <div style={{ background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '12px', overflow: 'hidden' }}>
        <div style={{ padding: '0.85rem 1rem', borderBottom: '1px solid #2a2a2a', fontSize: '0.85rem', color: '#888', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          Directors
        </div>
        {loading ? (
          <div style={{ padding: '1.5rem', color: '#666' }}>Loading…</div>
        ) : directorsWithActivity.length === 0 ? (
          <div style={{ padding: '1.5rem', color: '#666' }}>No directors found.</div>
        ) : (
          directorsWithActivity.map(d => {
            const totalGrantedFor = grants.filter(g => g.affiliate_id === d.id && g.status !== 'forfeited').reduce((s, g) => s + g.shares, 0);
            return (
              <div
                key={d.id}
                onClick={() => setSelectedId(d.id)}
                style={{
                  padding: '0.85rem 1rem',
                  borderTop: '1px solid #2a2a2a',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: '1rem', cursor: 'pointer',
                  background: selectedId === d.id ? '#2a1f14' : 'transparent',
                }}
                onMouseEnter={(e) => { if (selectedId !== d.id) e.currentTarget.style.background = '#1f1f1f'; }}
                onMouseLeave={(e) => { if (selectedId !== d.id) e.currentTarget.style.background = 'transparent'; }}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>
                    {d.name}
                    {d.tier === 'director' && <span style={{ marginLeft: 8, padding: '2px 6px', background: '#f0a50020', color: '#f0a500', borderRadius: 4, fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.04em' }}>DIRECTOR</span>}
                    {d.hasGrants && <span style={{ marginLeft: 6, padding: '2px 6px', background: '#10B98120', color: '#10B981', borderRadius: 4, fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.04em' }}>HAS GRANTS</span>}
                  </div>
                  <div style={{ color: '#888', fontSize: '0.78rem' }}>
                    {d.email || 'no email'} · code <code style={{ color: '#4ecca3' }}>{d.code}</code> · {d.qc} qualified customer{d.qc === 1 ? '' : 's'}
                  </div>
                </div>
                <div style={{ textAlign: 'right', fontSize: '0.85rem' }}>
                  <div style={{ color: '#f0a500', fontWeight: 700 }}>{totalGrantedFor.toLocaleString()}</div>
                  <div style={{ color: '#666', fontSize: '0.7rem' }}>granted</div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {selected && (
        <DirectorPanel
          director={selected}
          grants={grants.filter(g => g.affiliate_id === selected.id)}
          ladders={ladders.filter(l => l.affiliate_id === selected.id)}
          tranches={tranches}
          events={events.filter(e => e.affiliate_id === selected.id)}
          qc={selected.qc}
          adminEmail={adminEmail}
          onReload={loadAll}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, sub, accent }) {
  return (
    <div style={{ background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '10px', padding: '0.85rem 1rem' }}>
      <div style={{ color: '#888', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{label}</div>
      <div style={{ color: accent || '#e0e0e0', fontWeight: 700, fontSize: '1.4rem', fontFamily: '"JetBrains Mono", monospace' }}>{value}</div>
      {sub && <div style={{ color: '#666', fontSize: '0.72rem', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Director detail panel — appears as a sticky sheet under the list
// ---------------------------------------------------------------------------

function DirectorPanel({ director, grants, ladders, tranches, events, qc, adminEmail, onReload, onClose }) {
  const signingGrant = grants.find(g => g.grant_type === 'signing' && g.status !== 'forfeited');
  const milestoneGrants = grants.filter(g => g.grant_type === 'milestone');
  const signingTranches = signingGrant ? tranches.filter(t => t.grant_id === signingGrant.id) : [];

  return (
    <div style={{ marginTop: '1.5rem', background: '#1a1a1a', border: '1px solid #f0a50040', borderRadius: '12px', overflow: 'hidden' }}>
      <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #2a2a2a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700 }}>{director.name}</h2>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: '1.2rem' }}>×</button>
      </div>

      <div style={{ padding: '1.25rem' }}>
        {/* Signing Grant */}
        <SigningGrantSection
          director={director}
          grant={signingGrant}
          tranches={signingTranches}
          adminEmail={adminEmail}
          onReload={onReload}
        />

        {/* Milestone Ladder */}
        <MilestoneLadderSection
          director={director}
          ladders={ladders}
          grants={milestoneGrants}
          qc={qc}
          adminEmail={adminEmail}
          onReload={onReload}
        />

        {/* Activity */}
        <ActivitySection events={events} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Signing Grant section
// ---------------------------------------------------------------------------

function SigningGrantSection({ director, grant, tranches, adminEmail, onReload }) {
  const [showForm, setShowForm] = useState(false);
  const [shares, setShares] = useState('');
  const [signingDate, setSigningDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  const createSigningGrant = async () => {
    const n = parseInt(shares, 10);
    if (!Number.isFinite(n) || n <= 0) { alert('Enter shares > 0'); return; }
    setSaving(true);
    try {
      const { data: g, error: gErr } = await supabase.from('seu_grants').insert([{
        affiliate_id: director.id, grant_type: 'signing', shares: n,
        signing_date: signingDate, status: 'pending', granted_by_email: adminEmail,
      }]).select().single();
      if (gErr) throw gErr;

      // Split shares into 3 equal tranches with remainder on the last
      const base = Math.floor(n / 3);
      const rem = n - base * 3;
      const t1 = base, t2 = base, t3 = base + rem;
      const { error: tErr } = await supabase.from('seu_signing_tranches').insert([
        { grant_id: g.id, tranche_number: 1, months_after_vcd: 3, shares: t1 },
        { grant_id: g.id, tranche_number: 2, months_after_vcd: 4, shares: t2 },
        { grant_id: g.id, tranche_number: 3, months_after_vcd: 5, shares: t3 },
      ]);
      if (tErr) throw tErr;
      await logEvent(director.id, g.id, 'signing_grant_created', n, `Signing grant of ${n} SEUs created.`, adminEmail);
      setShowForm(false); setShares(''); onReload();
    } catch (e) {
      alert('Failed: ' + e.message);
    } finally { setSaving(false); }
  };

  const setVcd = async () => {
    if (!grant) return;
    const today = new Date().toISOString().slice(0, 10);
    if (!window.confirm(`Mark first Active Customer confirmed today (${today})? This starts the 3/4/5-month vesting clock.`)) return;
    const { error } = await supabase.from('seu_grants').update({ vesting_commencement_at: today, status: 'active' }).eq('id', grant.id);
    if (error) { alert('Failed: ' + error.message); return; }
    await logEvent(director.id, grant.id, 'vesting_commencement_set', null, `VCD set to ${today} (first Active Customer confirmed).`, adminEmail);
    onReload();
  };

  const vestTranche = async (t) => {
    if (!window.confirm(`Vest tranche ${t.tranche_number} (${t.shares} SEUs)? Confirm all ongoing conditions in Plan §5.2.4 are met.`)) return;
    const { error } = await supabase.from('seu_signing_tranches').update({ vested_at: new Date().toISOString(), vested_by_email: adminEmail }).eq('id', t.id);
    if (error) { alert('Failed: ' + error.message); return; }
    await logEvent(director.id, t.grant_id, 'tranche_vested', t.shares, `Signing grant tranche ${t.tranche_number} vested (${t.shares} SEUs).`, adminEmail);
    // If all 3 tranches vested, mark grant fully_vested
    const others = (await supabase.from('seu_signing_tranches').select('*').eq('grant_id', t.grant_id)).data || [];
    if (others.every(x => x.vested_at)) {
      await supabase.from('seu_grants').update({ status: 'fully_vested' }).eq('id', t.grant_id);
    }
    onReload();
  };

  const forfeitGrant = async () => {
    if (!grant) return;
    const reason = window.prompt('Forfeit reason (required):');
    if (!reason) return;
    const { error } = await supabase.from('seu_grants').update({
      status: 'forfeited', forfeited_at: new Date().toISOString(), forfeited_reason: reason,
    }).eq('id', grant.id);
    if (error) { alert('Failed: ' + error.message); return; }
    await logEvent(director.id, grant.id, 'forfeited', -grant.shares, `Signing grant forfeited: ${reason}`, adminEmail);
    onReload();
  };

  return (
    <section style={sectionStyle}>
      <header style={sectionHeader}>
        <h3 style={sectionTitle}>Signing Grant</h3>
        {!grant && (
          <button style={primaryBtn} onClick={() => setShowForm(s => !s)}>{showForm ? 'Cancel' : '+ Add'}</button>
        )}
      </header>

      {!grant && showForm && (
        <div style={formBox}>
          <label style={fieldLabel}>Shares</label>
          <input style={input} type="number" min="1" value={shares} onChange={e => setShares(e.target.value)} />
          <label style={fieldLabel}>Signing Date</label>
          <input style={input} type="date" value={signingDate} onChange={e => setSigningDate(e.target.value)} />
          <button style={primaryBtn} disabled={saving} onClick={createSigningGrant}>{saving ? 'Saving…' : 'Create grant'}</button>
        </div>
      )}

      {!grant && !showForm && <div style={{ color: '#666', fontSize: '0.85rem' }}>No signing grant yet.</div>}

      {grant && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <KV label="Total shares" value={grant.shares.toLocaleString()} />
            <KV label="Signed" value={grant.signing_date || '—'} />
            <KV label="VCD" value={grant.vesting_commencement_at || (
              <span style={{ color: '#f0a500' }}>not set</span>
            )} />
            <KV label="Status" value={grant.status} />
          </div>

          {!grant.vesting_commencement_at && (
            <button style={primaryBtn} onClick={setVcd}>Confirm first Active Customer (set VCD)</button>
          )}

          {grant.vesting_commencement_at && (
            <div style={{ marginTop: '0.5rem' }}>
              {tranches.map(t => (
                <div key={t.id} style={trancheRow}>
                  <div>
                    <div style={{ fontWeight: 600 }}>Tranche {t.tranche_number} <span style={{ color: '#888', fontWeight: 400 }}>· {t.months_after_vcd} mo · {t.shares} shares</span></div>
                    <div style={{ color: '#666', fontSize: '0.78rem' }}>
                      Eligible {addMonths(grant.vesting_commencement_at, t.months_after_vcd)}
                    </div>
                  </div>
                  {t.vested_at ? (
                    <span style={{ color: '#10B981', fontSize: '0.8rem', fontWeight: 600 }}>✓ Vested {t.vested_at.slice(0, 10)}</span>
                  ) : t.forfeited_at ? (
                    <span style={{ color: '#666', fontSize: '0.8rem' }}>Forfeited</span>
                  ) : (
                    <button style={primaryBtn} onClick={() => vestTranche(t)}>Vest</button>
                  )}
                </div>
              ))}
            </div>
          )}

          {grant.status !== 'forfeited' && (
            <button style={dangerBtnSm} onClick={forfeitGrant}>Forfeit signing grant</button>
          )}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Milestone Ladder section
// ---------------------------------------------------------------------------

function MilestoneLadderSection({ director, ladders, grants, qc, adminEmail, onReload }) {
  const [showForm, setShowForm] = useState(false);
  const [label, setLabel] = useState('');
  const [threshold, setThreshold] = useState('');
  const [shares, setShares] = useState('');
  const [saving, setSaving] = useState(false);

  const addRung = async () => {
    const t = parseInt(threshold, 10);
    const s = parseInt(shares, 10);
    if (!label.trim() || !Number.isFinite(t) || t <= 0 || !Number.isFinite(s) || s <= 0) {
      alert('Label, threshold, shares all required'); return;
    }
    setSaving(true);
    try {
      const { error } = await supabase.from('seu_milestone_ladders').insert([{
        affiliate_id: director.id, label: label.trim(), qualified_customer_threshold: t,
        shares: s, display_order: ladders.length,
      }]);
      if (error) throw error;
      await logEvent(director.id, null, 'milestone_added', null, `Milestone ${label.trim()} added: ${t} qualified customers = ${s} SEUs.`, adminEmail);
      setLabel(''); setThreshold(''); setShares(''); setShowForm(false); onReload();
    } catch (e) {
      alert('Failed: ' + e.message);
    } finally { setSaving(false); }
  };

  const certifyMilestone = async (ladder) => {
    if (!window.confirm(`Certify milestone ${ladder.label}? Creates a vested ${ladder.shares} SEU grant.`)) return;
    try {
      const { data: g, error: gErr } = await supabase.from('seu_grants').insert([{
        affiliate_id: director.id, grant_type: 'milestone', milestone_ladder_id: ladder.id,
        shares: ladder.shares, status: 'fully_vested', granted_by_email: adminEmail,
      }]).select().single();
      if (gErr) throw gErr;
      const { error: lErr } = await supabase.from('seu_milestone_ladders').update({
        achieved_at: new Date().toISOString(), certified_by_email: adminEmail,
      }).eq('id', ladder.id);
      if (lErr) throw lErr;
      await logEvent(director.id, g.id, 'milestone_certified', ladder.shares, `Milestone ${ladder.label} certified at ${ladder.qualified_customer_threshold} qualified customers (+${ladder.shares} SEUs).`, adminEmail);
      onReload();
    } catch (e) {
      alert('Failed: ' + e.message);
    }
  };

  const sorted = [...ladders].sort((a, b) => a.qualified_customer_threshold - b.qualified_customer_threshold);

  return (
    <section style={sectionStyle}>
      <header style={sectionHeader}>
        <h3 style={sectionTitle}>Milestone Ladder</h3>
        <button style={primaryBtn} onClick={() => setShowForm(s => !s)}>{showForm ? 'Cancel' : '+ Add rung'}</button>
      </header>

      {showForm && (
        <div style={formBox}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem' }}>
            <div>
              <label style={fieldLabel}>Label</label>
              <input style={input} placeholder="M1" value={label} onChange={e => setLabel(e.target.value)} />
            </div>
            <div>
              <label style={fieldLabel}>Threshold</label>
              <input style={input} type="number" placeholder="50" value={threshold} onChange={e => setThreshold(e.target.value)} />
            </div>
            <div>
              <label style={fieldLabel}>SEUs</label>
              <input style={input} type="number" placeholder="1000" value={shares} onChange={e => setShares(e.target.value)} />
            </div>
          </div>
          <button style={primaryBtn} disabled={saving} onClick={addRung}>{saving ? 'Saving…' : 'Add'}</button>
        </div>
      )}

      {sorted.length === 0 ? (
        <div style={{ color: '#666', fontSize: '0.85rem' }}>No milestone rungs yet.</div>
      ) : (
        <div>
          {sorted.map(l => {
            const pct = Math.min(100, Math.round((qc / l.qualified_customer_threshold) * 100));
            const ready = qc >= l.qualified_customer_threshold && !l.achieved_at;
            return (
              <div key={l.id} style={ladderRow}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontWeight: 700 }}>{l.label}</span>
                    <span style={{ color: '#888', fontSize: '0.85rem' }}>{l.qualified_customer_threshold} qualified · {l.shares.toLocaleString()} SEUs</span>
                  </div>
                  <div style={{ height: 6, background: '#0a0a0a', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: l.achieved_at ? '#10B981' : ready ? '#f0a500' : '#3a3a3a' }} />
                  </div>
                  <div style={{ color: '#666', fontSize: '0.72rem', marginTop: 3 }}>
                    {qc} / {l.qualified_customer_threshold} qualified customers ({pct}%)
                  </div>
                </div>
                <div style={{ width: 130, textAlign: 'right' }}>
                  {l.achieved_at ? (
                    <span style={{ color: '#10B981', fontWeight: 600, fontSize: '0.8rem' }}>✓ {l.achieved_at.slice(0, 10)}</span>
                  ) : ready ? (
                    <button style={primaryBtn} onClick={() => certifyMilestone(l)}>Certify</button>
                  ) : (
                    <span style={{ color: '#666', fontSize: '0.8rem' }}>Pending</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function ActivitySection({ events }) {
  if (events.length === 0) return null;
  return (
    <section style={sectionStyle}>
      <header style={sectionHeader}><h3 style={sectionTitle}>Activity</h3></header>
      <div>
        {events.slice(0, 20).map(e => (
          <div key={e.id} style={{ padding: '0.5rem 0', borderBottom: '1px solid #1f1f1f', fontSize: '0.85rem' }}>
            <div>{e.description || e.event_type}</div>
            <div style={{ color: '#666', fontSize: '0.72rem' }}>{new Date(e.occurred_at).toLocaleString()}{e.recorded_by_email ? ` · ${e.recorded_by_email}` : ''}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function logEvent(affiliateId, grantId, eventType, sharesChanged, description, byEmail) {
  await supabase.from('seu_vesting_events').insert([{
    affiliate_id: affiliateId, grant_id: grantId, event_type: eventType,
    shares_changed: sharesChanged, description, recorded_by_email: byEmail,
  }]);
}

function addMonths(isoDate, months) {
  const d = new Date(isoDate + 'T00:00:00');
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}

function KV({ label, value }) {
  return (
    <div style={{ background: '#0f0f0f', padding: '0.5rem 0.75rem', borderRadius: 6, border: '1px solid #2a2a2a' }}>
      <div style={{ color: '#666', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>{label}</div>
      <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{value}</div>
    </div>
  );
}

const sectionStyle = { marginBottom: '1.5rem', padding: '1rem 1.1rem', background: '#0f0f0f', borderRadius: '10px', border: '1px solid #2a2a2a' };
const sectionHeader = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' };
const sectionTitle = { margin: 0, fontSize: '0.95rem', fontWeight: 700, color: '#e0e0e0' };
const fieldLabel = { display: 'block', color: '#888', fontSize: '0.75rem', marginBottom: 4, marginTop: 8 };
const input = { width: '100%', boxSizing: 'border-box', padding: '0.55rem 0.7rem', background: '#0a0a0a', border: '1px solid #2a2a2a', borderRadius: 6, color: '#e0e0e0', fontSize: '0.9rem' };
const primaryBtn = { padding: '0.45rem 0.9rem', background: '#f0a500', border: 'none', borderRadius: 6, color: '#231F20', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' };
const dangerBtnSm = { marginTop: 8, padding: '0.4rem 0.8rem', background: 'transparent', border: '1px solid #e74c3c44', borderRadius: 6, color: '#e74c3c', fontSize: '0.78rem', cursor: 'pointer' };
const formBox = { padding: '0.75rem', background: '#0a0a0a', border: '1px solid #2a2a2a', borderRadius: 8, marginBottom: '0.75rem' };
const trancheRow = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.55rem 0.6rem', background: '#0a0a0a', border: '1px solid #2a2a2a', borderRadius: 6, marginBottom: 6 };
const ladderRow = { display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.65rem 0.6rem', background: '#0a0a0a', border: '1px solid #2a2a2a', borderRadius: 6, marginBottom: 6 };
